import { useState } from "react";
import { useTranslation } from "react-i18next";
import { hrApi, useInbox } from "../../hooks/hr";
import { useLocale } from "../../hooks/useLocale";
import { money } from "../../lib/format";
import type { InboxItem } from "../../lib/typesHr";
import { Empty, SectionHeader } from "../ui/Badges";
import { dateOnly, ReasonDialog, useAction, useEmpName } from "./shared";

type D = Record<string, string | number | null | undefined>;

/** One line describing what is being asked, per request type. */
function Summary({ item }: { item: InboxItem }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const d = (item.detail ?? {}) as D;
  const num = (v: unknown) => <span className="num font-bold">{String(v)}</span>;
  if (item.entity_type === "leave_request")
    return <span>{t("hr.inbox.leave", { type: locale === "ar" && d.type_name_ar ? String(d.type_name_ar) : String(d.type_name ?? ""), days: Number(d.days ?? 0) })} · {num(dateOnly(String(d.start_date), locale))} → {num(dateOnly(String(d.end_date), locale))}{d.reason ? ` · ${d.reason}` : ""}</span>;
  if (item.entity_type === "loan")
    return <span>{t(`hr.loanKind.${d.kind ?? "advance"}`)} · {num(money(Number(d.principal_fils ?? 0)))} {t("currency")} · {t("hr.installmentsCount", { count: Number(d.installments_count ?? 0) })}{d.reason ? ` · ${d.reason}` : ""}</span>;
  if (item.entity_type === "adjustment")
    return <span>{t(`hr.adjKind.${d.kind ?? "bonus"}`)} · {String(d.type_name ?? "")} · {num(money(Number(d.amount_fils ?? 0)))} {t("currency")}{d.reason ? ` · ${d.reason}` : ""}</span>;
  return <span>{t("hr.inbox.correction")} · {num(dateOnly(String(d.work_date), locale))}{d.reason ? ` · ${d.reason}` : ""}</span>;
}

export function ApprovalsPanel() {
  const { t } = useTranslation();
  const inbox = useInbox();
  const name = useEmpName();
  const { act, busy } = useAction();
  const [rejecting, setRejecting] = useState<InboxItem | null>(null);

  const decide = (i: InboxItem, decision: "approve" | "reject", note?: string) =>
    act(() => hrApi.post("/decide", { entityType: i.entity_type, entityId: i.entity_id, decision, note }), decision === "approve" ? "hr.approved" : "hr.rejected");

  return (
    <section className="card space-y-3 p-5" aria-label={t("hr.tabs.approvals")}>
      <SectionHeader icon="✅" title={t("hr.tabs.approvals")} subtitle={t("hr.inbox.subtitle")} />
      {(inbox.data ?? []).length === 0 && <Empty>{t("hr.inbox.empty")}</Empty>}
      <ul className="space-y-2">
        {(inbox.data ?? []).map((i) => (
          <li key={i.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-3">
            <div className="min-w-0 space-y-0.5">
              <div className="text-sm font-black">{name(i.employee)} <span className="num font-mono text-[10px] font-bold text-gray-400">{i.employee?.employee_no}</span></div>
              <div className="text-xs font-medium text-gray-600"><Summary item={i} /></div>
              <div className="text-[10px] font-semibold text-gray-400">{t("hr.inbox.step", { step: i.step_no })} · {t(`hr.role.${i.approver_role}`, { defaultValue: i.approver_role })}</div>
            </div>
            <div className="flex gap-2">
              <button type="button" className="btn-success" disabled={busy} onClick={() => void decide(i, "approve")}>{t("hr.approve")}</button>
              <button type="button" className="btn-ghost text-red-700" disabled={busy} onClick={() => setRejecting(i)}>{t("hr.reject")}</button>
            </div>
          </li>
        ))}
      </ul>
      <ReasonDialog open={!!rejecting} title={t("hr.rejectTitle")} confirmLabel={t("hr.reject")} onClose={() => setRejecting(null)} onConfirm={(note) => { const i = rejecting!; setRejecting(null); void decide(i, "reject", note); }} />
    </section>
  );
}
